import React, { useState, useRef, useEffect } from 'react';
import { ViewMode, User } from '../types';
import { MessageSquare, FileText, Split, Wifi, WifiOff, Menu, X, LogOut, User as UserIcon, Settings, ChevronDown, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import ChangePasswordModal from './ChangePasswordModal';

interface HeaderProps {
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  user?: User | null;
  backendConnected: boolean | null;
  onShowApiLogs?: () => void;
  isTreatmentPlanContext?: boolean;
}

const Header: React.FC<HeaderProps> = ({ viewMode, onViewModeChange, user, backendConnected, onShowApiLogs, isTreatmentPlanContext = false }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { signOut } = useAuth();
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    navigate('/signin');
  };

  const handleSettings = () => {
    setShowUserMenu(false);
    // Navigate to settings or open settings modal
    // For now, we'll just close the menu
    console.log('Settings clicked');
  };
  
  const handleChangePassword = () => {
    setShowUserMenu(false);
    setShowChangePasswordModal(true);
  };
  
  return (
    <div className="bg-white border-b border-medical-200 px-4 sm:px-6 h-[73px] flex items-center">
      <div className="flex items-center justify-between w-full">
        {/* Brand */}
        <div className="flex items-center space-x-2 sm:space-x-4">
          <a 
            href="https://www.orthomind.ai/" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center space-x-2 sm:space-x-3 hover:opacity-80 transition-opacity"
          >
            {/* Logo */}
            <img 
              src="/logo_small.png" 
              alt="OrthoMind Logo" 
              className="w-6 h-6 sm:w-8 sm:h-8 cursor-pointer"
            />
            <div>
              <h1 className="text-base sm:text-xl font-semibold text-medical-900">ORTHOMIND</h1>
              <p className="hidden sm:block text-sm text-medical-600">AI Clinical Decision Support for Orthodontists</p>
            </div>
          </a>
        </div>

        {/* Right side content */}
        <div className="flex items-center space-x-4">
          {/* User Menu */}
          {user && (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-2 px-3 py-2 rounded-lg border border-medical-200 hover:bg-medical-50 transition-all duration-200 group"
              >
                <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <UserIcon className="w-5 h-5 text-primary-600" />
                </div>
                <span className="text-sm font-medium text-medical-900 hidden sm:block">{user.name || user.email}</span>
                <ChevronDown className={`w-4 h-4 text-medical-600 transition-transform duration-200 ${showUserMenu ? 'rotate-180' : ''}`} />
              </button>
              
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-medical-200 overflow-hidden z-50">
                  {/* User Info Header */}
                  <div className="px-4 py-3 bg-medical-50 border-b border-medical-200">
                    <p className="text-sm font-semibold text-medical-900">{user.name}</p>
                    <p className="text-xs text-medical-600 mt-0.5">{user.email}</p>
                  </div>
                  
                  {/* Menu Items */}
                  <div className="py-1">
                    <button
                      onClick={handleSettings}
                      className="w-full text-left px-4 py-2.5 text-sm text-medical-700 hover:bg-medical-50 flex items-center space-x-3 transition-colors"
                    >
                      <Settings className="w-4 h-4 text-medical-500" />
                      <span>Settings</span>
                    </button>
                    
                    <button
                      onClick={handleChangePassword}
                      className="w-full text-left px-4 py-2.5 text-sm text-medical-700 hover:bg-medical-50 flex items-center space-x-3 transition-colors"
                    >
                      <Lock className="w-4 h-4 text-medical-500" />
                      <span>Change Password</span>
                    </button>
                    
                    <div className="border-t border-medical-100 my-1"></div>
                    
                    <button
                      onClick={handleSignOut}
                      className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-3 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* Connection Status - Removed for production */}
          {/* <div className="hidden sm:flex items-center space-x-2">
            {backendConnected === null ? (
              <div className="flex items-center space-x-1 text-medical-400">
                <div className="w-2 h-2 bg-medical-400 rounded-full animate-pulse"></div>
                <span className="text-xs">Connecting...</span>
              </div>
            ) : backendConnected ? (
              <div className="flex items-center space-x-1 text-green-600">
                <Wifi className="w-4 h-4" />
                <span className="text-xs">Connected</span>
              </div>
            ) : (
              <div className="flex items-center space-x-1 text-red-600">
                <WifiOff className="w-4 h-4" />
                <span className="text-xs">Offline</span>
              </div>
            )}
          </div> */}

          {/* View Mode Toggle - Desktop - Only show in treatment plan context */}
          {isTreatmentPlanContext && (
            <div className="hidden sm:flex items-center space-x-2">
              <span className="text-sm text-medical-600 mr-2">View:</span>
              <div className="flex bg-medical-100 rounded-lg p-1">
            <button
              onClick={() => onViewModeChange('chat')}
              className={`
                flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors
                ${viewMode === 'chat'
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-medical-600 hover:text-medical-800'
                }
              `}
            >
              <MessageSquare className="w-4 h-4" />
              <span>Chat</span>
            </button>

            <button
              onClick={() => onViewModeChange('document')}
              className={`
                flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors
                ${viewMode === 'document'
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-medical-600 hover:text-medical-800'
                }
              `}
            >
              <FileText className="w-4 h-4" />
              <span>Document</span>
            </button>

            <button
              onClick={() => onViewModeChange('split')}
              className={`
                flex items-center space-x-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors
                ${viewMode === 'split'
                  ? 'bg-white text-primary-700 shadow-sm'
                  : 'text-medical-600 hover:text-medical-800'
                }
              `}
            >
              <Split className="w-4 h-4" />
              <span>Split</span>
            </button>
              </div>
            </div>
          )}
          
          {/* Mobile Menu Button */}
          <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="sm:hidden p-2 rounded-lg hover:bg-medical-100 transition-colors"
        >
          {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      
      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="sm:hidden mt-3 pb-3 border-t border-medical-200 pt-3">
          {/* Connection Status for Mobile - Removed for production */}
          {/* <div className="flex items-center justify-center mb-3">
            {backendConnected === null ? (
              <div className="flex items-center space-x-1 text-medical-400">
                <div className="w-2 h-2 bg-medical-400 rounded-full animate-pulse"></div>
                <span className="text-xs">Connecting...</span>
              </div>
            ) : backendConnected ? (
              <div className="flex items-center space-x-1 text-green-600">
                <Wifi className="w-4 h-4" />
                <span className="text-xs">Connected</span>
              </div>
            ) : (
              <div className="flex items-center space-x-1 text-red-600">
                <WifiOff className="w-4 h-4" />
                <span className="text-xs">Offline</span>
              </div>
            )}
          </div> */}
          
          {/* View Mode Buttons for Mobile - Only show in treatment plan context */}
          {isTreatmentPlanContext && (
            <div className="flex flex-col space-y-2">
            <button
              onClick={() => {
                onViewModeChange('chat');
                setIsMobileMenuOpen(false);
              }}
              className={`
                flex items-center justify-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
                ${viewMode === 'chat'
                  ? 'bg-primary-600 text-white'
                  : 'bg-medical-100 text-medical-700 hover:bg-medical-200'
                }
              `}
            >
              <MessageSquare className="w-4 h-4" />
              <span>Chat View</span>
            </button>
            
            <button
              onClick={() => {
                onViewModeChange('document');
                setIsMobileMenuOpen(false);
              }}
              className={`
                flex items-center justify-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors
                ${viewMode === 'document'
                  ? 'bg-primary-600 text-white'
                  : 'bg-medical-100 text-medical-700 hover:bg-medical-200'
                }
              `}
            >
              <FileText className="w-4 h-4" />
              <span>Document View</span>
            </button>
            
            <button
              onClick={() => {
                onViewModeChange('split');
                setIsMobileMenuOpen(false);
              }}
              className={`
                sm:hidden flex items-center justify-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium
                bg-medical-100 text-medical-400 cursor-not-allowed
              `}
              disabled
            >
              <Split className="w-4 h-4" />
              <span>Split View (Desktop Only)</span>
            </button>
            </div>
          )}
        </div>
      )}
      
      {/* Change Password Modal */}
      <ChangePasswordModal 
        isOpen={showChangePasswordModal} 
        onClose={() => setShowChangePasswordModal(false)} 
      />
    </div>
  );
};

export default Header;
